const normalizeEditorDate = (value) => {
  if (!value) {
    return '';
  }

  const rawValue = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(rawValue)) {
    const [first, second, year] = rawValue.split('/');
    const day = first;
    const month = second;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(rawValue)) {
    const [day, month, year] = rawValue.split('-');
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


const airlineColumnKey = (name) =>
  `airline__${String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}`;


const calcAirlineSum = (row, airlineNames = []) =>
  airlineNames.reduce((sum, airlineName) => sum + Number(row[airlineColumnKey(airlineName)] || 0), 0);


const calcEntrySum = (row) =>
  Number(row.dy_pax_entry_app || 0) + Number(row.dy_pax_entry_otdy_kiosks || 0);


const calcDyDiff = (row) => Number(row.dy_pax || 0) - calcEntrySum(row);


const buildUsageSheetRow = (record, airlineNames = []) => {
  const row = {
    id: record.id,
    airport_code: record.airport_code,
    flight_date: normalizeEditorDate(record.flight_date),
    status: record.status,
    total_dep_pax: Number(record.total_dep_pax || 0),
    dy_pax: Number(record.dy_pax || 0),
    dy_pax_percentage: Number(record.dy_pax_percentage || 0),
    dy_pax_entry_app: Number(record.dy_pax_entry_app || 0),
    dy_pax_entry_otdy_kiosks: Number(record.dy_pax_entry_otdy_kiosks || 0),
    dy_pax_pesc: Number(record.dy_pax_pesc || 0),
    dy_pax_boarding: Number(record.dy_pax_boarding || 0),
    entry_gap: Number(record.entry_gap || 0),
    airline_gap: Number(record.airline_gap || 0),
    has_mismatch: Boolean(record.has_mismatch),
    remarks: record.remarks || '',
  };

  airlineNames.forEach((airlineName) => {
    row[airlineColumnKey(airlineName)] = Number(record?.airlines?.[airlineName] || 0);
  });

  return row;
};


const buildUsagePayload = (row, airlineNames = []) => {
  const airlines = {};
  airlineNames.forEach((airlineName) => {
    const pax = Number(row[airlineColumnKey(airlineName)] || 0);
    if (pax > 0) {
      airlines[airlineName] = pax;
    }
  });

  const dyPax = Number(row.dy_pax || 0);
  const totalDepPax = Number(row.total_dep_pax || 0);

  return {
    airport_code: row.airport_code,
    flight_date: row.flight_date,
    status: row.status,
    total_dep_pax: totalDepPax,
    dy_pax: dyPax,
    dy_pax_percentage: totalDepPax ? Number(((dyPax * 100) / totalDepPax).toFixed(2)) : 0,
    dy_pax_entry_app: Number(row.dy_pax_entry_app || 0),
    dy_pax_entry_otdy_kiosks: Number(row.dy_pax_entry_otdy_kiosks || 0),
    dy_pax_pesc: Number(row.dy_pax_pesc || 0),
    dy_pax_boarding: Number(row.dy_pax_boarding || 0),
    remarks: row.remarks || '',
    airlines,
  };
};


const hasUsageRowChanged = (row, originalRow, airlineNames = []) => {
  if (!originalRow) {
    return true;
  }

  const nextPayload = buildUsagePayload(row, airlineNames);
  const prevPayload = buildUsagePayload(originalRow, airlineNames);
  return JSON.stringify(nextPayload) !== JSON.stringify(prevPayload);
};


export {
  airlineColumnKey,
  calcAirlineSum,
  calcEntrySum,
  calcDyDiff,
  buildUsagePayload,
  buildUsageSheetRow,
  hasUsageRowChanged,
};
